import "./banner.css";
import { ConsentStore } from "./store";

type Mode = "summary" | "manage";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const c of children) node.append(typeof c === "string" ? document.createTextNode(c) : c);
  return node;
}

export interface BannerController {
  open(mode?: Mode): void;
  close(): void;
  destroy(): void;
  element: HTMLElement;
}

export function mountConsentBanner(store: ConsentStore): BannerController {
  const existing = document.getElementById("ror-consent");
  if (existing) existing.remove();

  const root = el("aside", {
    id: "ror-consent",
    class: "ror-consent",
    role: "dialog",
    "aria-modal": "false",
    "aria-labelledby": "ror-consent-title",
    "aria-describedby": "ror-consent-body",
    "data-mode": "summary",
    hidden: "",
  });

  const title = el("h2", { id: "ror-consent-title", class: "ror-consent__title" }, [
    "Your privacy choices",
  ]);

  const bodyLink = el("a", { href: "/privacy" }, ["privacy policy"]);
  const body = el("p", { id: "ror-consent-body", class: "ror-consent__body" }, [
    "We use only essential storage to remember your calculator inputs. Analytics and ads stay off until you allow them. See our ",
    bodyLink,
    ".",
  ]);

  const textWrap = el("div", { class: "ror-consent__text" }, [title, body]);

  const acceptBtn = el(
    "button",
    { type: "button", class: "ror-consent__btn ror-consent__btn--primary" },
    ["Accept all"],
  );
  const rejectBtn = el(
    "button",
    { type: "button", class: "ror-consent__btn ror-consent__btn--secondary" },
    ["Reject non-essential"],
  );
  const manageBtn = el(
    "button",
    {
      type: "button",
      class: "ror-consent__btn ror-consent__btn--secondary",
      "aria-expanded": "false",
      "aria-controls": "ror-consent-manage",
    },
    ["Manage"],
  );

  const actions = el("div", { class: "ror-consent__actions" }, [
    manageBtn,
    rejectBtn,
    acceptBtn,
  ]);

  const analyticsInput = el("input", {
    type: "checkbox",
    id: "ror-consent-analytics",
  }) as HTMLInputElement;
  const advertisingInput = el("input", {
    type: "checkbox",
    id: "ror-consent-advertising",
  }) as HTMLInputElement;

  const manageRegion = el(
    "div",
    { id: "ror-consent-manage", class: "ror-consent__manage", hidden: "" },
    [
      el("div", { class: "ror-consent__row" }, [
        el("input", {
          type: "checkbox",
          checked: "",
          disabled: "",
          "aria-describedby": "ror-consent-essential-desc",
          id: "ror-consent-essential",
        }),
        el("div", {}, [
          el("label", { for: "ror-consent-essential" }, ["Essential (always on)"]),
          el("p", { id: "ror-consent-essential-desc" }, [
            "Remembers your calculator inputs and your consent choice. Never leaves your browser.",
          ]),
        ]),
      ]),
      el("div", { class: "ror-consent__row" }, [
        analyticsInput,
        el("div", {}, [
          el("label", { for: "ror-consent-analytics" }, ["Analytics"]),
          el("p", {}, [
            "Aggregate, no-cookie page counts so we can see which pages are useful. No PII, no fingerprinting.",
          ]),
        ]),
      ]),
      el("div", { class: "ror-consent__row" }, [
        advertisingInput,
        el("div", {}, [
          el("label", { for: "ror-consent-advertising" }, ["Advertising"]),
          el("p", {}, [
            "Lets ad partners (Google AdSense, Ezoic, Media.net) set cookies to show more relevant ads. We do not run ads today; this stays off unless you turn it on.",
          ]),
        ]),
      ]),
      el(
        "div",
        { class: "ror-consent__actions" },
        [
          el(
            "button",
            {
              type: "button",
              class: "ror-consent__btn ror-consent__btn--primary",
              id: "ror-consent-save",
            },
            ["Save choices"],
          ),
        ],
      ),
    ],
  );

  const inner = el("div", { class: "ror-consent__inner" }, [
    textWrap,
    actions,
    manageRegion,
  ]);

  root.append(inner);
  document.body.append(root);

  const setMode = (mode: Mode) => {
    root.setAttribute("data-mode", mode);
    if (mode === "manage") {
      manageRegion.hidden = false;
      manageBtn.setAttribute("aria-expanded", "true");
      const current = store.getState();
      analyticsInput.checked = current.analytics;
      advertisingInput.checked = current.advertising;
    } else {
      manageRegion.hidden = true;
      manageBtn.setAttribute("aria-expanded", "false");
    }
  };

  const open = (mode: Mode = "summary") => {
    setMode(mode);
    root.hidden = false;
  };

  const close = () => {
    root.hidden = true;
  };

  acceptBtn.addEventListener("click", () => {
    store.acceptAll();
    close();
  });
  rejectBtn.addEventListener("click", () => {
    store.rejectAll();
    close();
  });
  manageBtn.addEventListener("click", () => {
    setMode(root.getAttribute("data-mode") === "manage" ? "summary" : "manage");
  });
  manageRegion.querySelector<HTMLButtonElement>("#ror-consent-save")?.addEventListener(
    "click",
    () => {
      store.set({
        analytics: analyticsInput.checked,
        advertising: advertisingInput.checked,
      });
      close();
    },
  );

  if (!store.hasMadeChoice()) open("summary");

  return {
    open,
    close,
    destroy: () => root.remove(),
    element: root,
  };
}
